import { useTolgee, useTranslate } from '@tolgee/react';
import { Alert, Button, Col, Image, List, Row, Typography } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { difference } from 'lodash';
import { useMemo, useState } from 'react';
import { PERMISSION_CODES } from '../../../constants/permissionCodes';
import {
  useGlobalActions,
  useGlobalContext,
} from '../../../global-context/GlobalContext';
import { useCreateSpecialOrder } from '../../../hooks/graphql/useCreateSpecialOrder';
import { useUpdateSpecialOrder } from '../../../hooks/graphql/useUpdateSpecialOrder';
import useIsMobile from '../../../hooks/utils/useIsMobile';
import { productInCart } from '../../../hooks/utils/useOrderDetails';
import { SupportedLanguage } from '../../../interfaces/ISpecialOrder';
import { isoStringToLocalDate } from '../../../utils/calendarUtility';
import checkExists from '../../../utils/checkExists';
import { showToast } from '../../../utils/commonUtility';
import Can from '../../common/Can';
import {
  CartOrderConfirmation,
  ConfirmationModal,
} from '../../requests/confirmation-modal/ConfirmationModal';
import { QuantitySelectorHorizontal } from '../quantity-selector/QuantitySelector';
import './CheckoutPage.scss';

const { Text, Title } = Typography;

const baseUrl = process.env.REACT_APP_BASE_URL_IMAGE;

const CheckoutPage = ({
  selectedDate,
}: {
  selectedDate: {
    date: string;
    day: string;
  } | null;
}) => {
  const specialOrderData = useGlobalContext(c => c.specialOrder);
  const { t } = useTranslate();
  const cartItems = useGlobalContext(c => c.specialOrder.cartItems);

  const itemsInCart = useGlobalContext(c => c.specialOrder.itemsInCart);
  const existingCartItems = useGlobalContext(c => c.specialOrder.existingCart);

  const specialOrderRules = useGlobalContext(
    c => c.specialOrder.specialOrderRules,
  );

  /**
   * fetch standard delivery charge, repeating order delivery charge and subscriptions product ids
   * repeating order delivery charge - when user purchase subscription products then apply repeating order delivery charges
   * standard delivery charge - when user purchase any other products then apply standard delivery charges
   * subscriptions product ids - subscription products compare user purchase which product purchase subscription product or any other products
   */
  const {
    standardDeliveryCharge,
    subscriptionProductIds,
    repeatingOrderDeliveryCharge,
    previousDeliveryCharge,
  } = useGlobalContext(c => c.specialOrder.deliveryCharges);

  const [confirmationProps, setConfirmationProps] = useState<boolean>(false);
  const data = Array.from(cartItems.values());
  const {
    setExistingOrderOnDay,
    setSpecialOrderComments,
    handleSetExistingComment,
    setDeliveryCharges,
  } = useGlobalActions();
  const { createSpecialOrder, createOrderLoading } = useCreateSpecialOrder();
  const { updateSpecialOrder, updateOrderLoading } = useUpdateSpecialOrder();
  const totalOrderPricing = useGlobalContext(c => c.specialOrder.totalCartCost);

  const getDeliveryCharge = () => {
    const allMatchProducts = data?.every(
      ({ specialOrderQuantity, cartQuantity }) =>
        specialOrderQuantity === cartQuantity,
    );

    // If all cartQuantities are 0, return 0
    if (itemsInCart === 0) {
      return 0;
    }

    if (specialOrderData.specialOrderId && allMatchProducts) {
      return previousDeliveryCharge;
    }

    const specialOrderProductIds = data
      .map(({ productId, cartQuantity }) => {
        if (cartQuantity) {
          return productId;
        }
        return;
      })
      .filter(checkExists);

    /**
     * Check if all products are Subscription products by comparing the product IDs
     * If there is a difference between the specialOrderProductIds and subscriptionProductIds,
     * it means some products are not Subscription products.
     */
    const isSubscriptionProducts = difference(
      specialOrderProductIds,
      subscriptionProductIds,
    );

    if (!isSubscriptionProducts.length) {
      return repeatingOrderDeliveryCharge;
    }

    return standardDeliveryCharge;
  };

  const deliveryCharges = useMemo(getDeliveryCharge, [
    standardDeliveryCharge,
    data,
    repeatingOrderDeliveryCharge,
    subscriptionProductIds,
    specialOrderData,
    previousDeliveryCharge,
  ]);

  const specialOrderValidation = useMemo(() => {
    const mainDishSpecialOrderRule = specialOrderRules?.find(
      ({ productType }) => productType === 'Main',
    );
    if (!mainDishSpecialOrderRule?.minQuantity) {
      return { isValid: true, message: '', minQuantity: 0 };
    }
    let isValid = false;
    let message = '';

    for (const rule of specialOrderRules) {
      if (rule.minQuantity <= 0) {
        continue;
      }

      const totalQuantityForType = data.reduce((acc, item) => {
        if (
          item.productType === rule.productType
          // && item.isFruit === rule.isFruit
        ) {
          return acc + item.cartQuantity;
        }
        return acc;
      }, 0);

      if (totalQuantityForType >= rule.minQuantity) {
        isValid = true;
        message = 'Special order rule satisfied.';
        break;
      }
    }

    const { minQuantity } = mainDishSpecialOrderRule;

    return { isValid, message, minQuantity };
  }, [data, specialOrderRules]);

  if (!data.length && !specialOrderData.specialOrderId) {
    return (
      <strong style={{ fontSize: '2rem' }}>
        {t('no-items-added-to-cart')}
      </strong>
    );
  }

  const placeOrder = () => {
    const deliveryCharge = getDeliveryCharge();

    if (specialOrderData.specialOrderId) {
      const updatedOrderData = data
        .filter(o => o.cartQuantity)
        .map(o => ({
          productId: o.productId,

          quantity: o.cartQuantity,
        }));
      updateSpecialOrder(
        specialOrderData.specialOrderId,
        specialOrderData.specialOrderComments,
        updatedOrderData,
        res => {
          if (res?.updateCompanySpecialOrder?.data?.specialOrderId) {
            setExistingOrderOnDay(data, specialOrderData.specialOrderId ?? 0);
            handleSetExistingComment(specialOrderData.specialOrderComments);

            if (!updatedOrderData.length) {
              showToast('order-updated', 'info');
              return;
            }
            setConfirmationProps(true);
          } else {
            showToast('something-wrong', 'error');
          }
        },
      );
    } else {
      createSpecialOrder(
        isoStringToLocalDate(selectedDate?.date).toISOString() ?? '',
        Number(selectedDate?.day) ?? 1,
        specialOrderData.specialOrderComments,
        data.map(o => ({
          productId: o.productId,
          quantity: o.cartQuantity,
        })),
        res => {
          if (res?.createCompanySpecialOrder?.data?.specialOrderId) {
            setExistingOrderOnDay(
              data,
              res?.createCompanySpecialOrder?.data?.specialOrderId,
            );
            handleSetExistingComment(specialOrderData.specialOrderComments);
            setConfirmationProps(true);
          } else {
            showToast('something-wrong', 'error');
          }
        },
      );
    }

    setDeliveryCharges({
      standardDeliveryCharge,
      subscriptionProductIds,
      repeatingOrderDeliveryCharge,
      previousDeliveryCharge: deliveryCharge,
    });
  };

  return (
    <div className='checkout-container'>
      <div className='checkout-list-section'>
        <CheckoutListHeader />
        <RenderCheckoutList data={data} existingCartItems={existingCartItems} />
      </div>
      <div className='checkout-list-placeorder'>
        <div className='checkout-placeorder-heading'>{t('cart-total')}</div>
        <div className='checkout-placeorder-total'>
          <span>{t('cart-value')}</span>
          <span>CHF {totalOrderPricing.toFixed(2)}</span>
        </div>
        {deliveryCharges > 0 && (
          <div className='checkout-placeorder-total'>
            <span>{t('delivery-charge')}</span>
            <span>
              CHF{' '}
              {totalOrderPricing > 0
                ? Number(deliveryCharges).toFixed(2)
                : Number(0).toFixed(2)}
            </span>
          </div>
        )}
        <div className='checkout-placeorder-total total'>
          <span>{t('total')}</span>
          <span>
            CHF{' '}
            {totalOrderPricing > 0
              ? (Number(totalOrderPricing) + Number(deliveryCharges)).toFixed(2)
              : Number(0).toFixed(2)}
          </span>
        </div>
        <div className='checkout-placeorder-heading'>{t('comments')}</div>
        <TextArea
          style={{ margin: '1rem 0', width: '100%' }}
          placeholder={t('comments')}
          value={specialOrderData.specialOrderComments}
          onChange={e => {
            setSpecialOrderComments(e.target.value);
          }}
        />

        <Can
          permission={
            specialOrderData.specialOrderId && specialOrderData.existingCartSize
              ? PERMISSION_CODES.SPECIAL_ORDERS_EDIT
              : PERMISSION_CODES.SPECIAL_ORDERS_CREATE
          }
        >
          <Button
            className='checkout-placeorder-btn'
            onClick={placeOrder}
            loading={createOrderLoading || updateOrderLoading}
            disabled={Boolean(
              (specialOrderData.specialOrderId &&
                !specialOrderData.isExistingCartUpdated) ||
                specialOrderValidation.isValid === false,
            )}
          >
            {specialOrderData.specialOrderId &&
            specialOrderData.existingCartSize
              ? t('update-order')
              : t('confirm-order')}
          </Button>
        </Can>

        {Boolean(specialOrderValidation.isValid === false) && (
          <Alert
            message={t('special-order-validation.error', {
              minQuantity: specialOrderValidation.minQuantity,
            })}
            type='error'
            style={{ margin: '1rem 0' }}
          />
        )}

        <ConfirmationModal
          isOpen={confirmationProps}
          setOpen={(bool: boolean) => {
            setConfirmationProps(bool);
          }}
        >
          <CartOrderConfirmation />
        </ConfirmationModal>
      </div>
    </div>
  );
};

const CheckoutListHeader = () => {
  const { t } = useTranslate();

  const isMobile = useIsMobile();

  return (
    <Row
      style={{
        fontWeight: 'bold',
        fontSize: 14,
        textAlign: 'center',
        marginBottom: 16,
      }}
    >
      <Col style={{ fontWeight: 'bold' }} span={isMobile ? 8 : 5}></Col>
      <Col style={{ fontWeight: 'bold' }} span={isMobile ? 8 : 6}>
        {t('product')}
      </Col>
      <Col
        style={{ fontWeight: 'bold' }}
        span={isMobile ? 8 : 4}
        className='price-column'
      >
        {t('price')}
      </Col>
      {!isMobile && (
        <>
          <Col style={{ fontWeight: 'bold' }} span={4}>
            {t('quantity')}
          </Col>
          <Col style={{ fontWeight: 'bold' }} span={4}>
            {t('sub-total')}
          </Col>
        </>
      )}
    </Row>
  );
};

const RenderCheckoutList = ({
  data,
  existingCartItems,
}: {
  data: productInCart[];
  existingCartItems: Map<number, productInCart>;
}) => {
  const isMobile = useIsMobile();
  const { t } = useTranslate();

  const { getLanguage } = useTolgee();
  const language = getLanguage() as SupportedLanguage;
  const { handleProductName } = useGlobalActions();

  return (
    <List
      dataSource={data}
      renderItem={product => (
        <Row
          key={product.productId}
          style={{
            padding: '1rem 0',
            borderBottom: '1px solid #f0f0f0',
            alignItems: 'center',
          }}
        >
          <Col span={isMobile ? 8 : 5} className='product-info'>
            <Image
              width={isMobile ? 80 : '100%'}
              src={`${baseUrl}${product.productImage}`}
              preview={false}
            />
          </Col>
          <Col span={isMobile ? 8 : 6} className='product-name'>
            <Text
              style={{
                fontSize: '1.2rem',
                fontWeight: 600,
              }}
            >
              {handleProductName(product.productNames, language).name}
            </Text>
          </Col>
          <Col span={isMobile ? 8 : 4} className='product-info'>
            <Text>CHF {product.price.toFixed(2)}</Text>
          </Col>
          {!isMobile ? (
            <>
              <Col
                span={4}
                style={{ display: 'flex', justifyContent: 'center' }}
              >
                <QuantitySelectorHorizontal
                  quantity={product.cartQuantity}
                  foodInfo={product}
                />
              </Col>
              <Col span={4}>
                <Text>
                  CHF {(product.price * product.cartQuantity).toFixed(2)}
                </Text>
              </Col>
            </>
          ) : (
            <Col span={24} style={{ marginTop: '1rem' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <QuantitySelectorHorizontal
                  quantity={product.cartQuantity}
                  foodInfo={product}
                />
                <Text strong>
                  CHF {(product.price * product.cartQuantity).toFixed(2)}
                </Text>
              </div>
            </Col>
          )}
          {Boolean(
            existingCartItems &&
              existingCartItems.get(product.productId) &&
              existingCartItems.get(product.productId)?.specialOrderQuantity !=
                product.cartQuantity,
          ) && (
            <div className='cart-updated-warning'>
              <span className='warning'>
                <p>!</p>
              </span>{' '}
              {t('quantity-changed-from')}{' '}
              <span className='number'>
                {
                  existingCartItems.get(product.productId)?.specialOrderQuantity
                }{' '}
              </span>
              {t('quantity-changed-to')}{' '}
              <span className='number'>{product.cartQuantity}</span>{' '}
              {t('quantity-change')} dishes
            </div>
          )}
        </Row>
      )}
    />
  );
};

export default CheckoutPage;
